import { useEffect, useRef, useState } from 'react';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useCardStyles } from './cardStyles';
import { humanState, useEntity, useEntityFriendlyName } from './useEntity';
import { sensorIconFor } from './icons';
import { domainForEntityId } from './EntityCard';

type Trend = 'up' | 'down' | 'stable';

export interface SensorCardProps {
  entityId: string;
  title?: string;
}

const TONE_COLOR: Record<string, string> = {
  on: 'text-emerald-500 dark:text-emerald-400',
  off: 'text-gray-400 dark:text-gray-500',
  warn: 'text-amber-500 dark:text-amber-400',
  muted: 'text-gray-400 dark:text-gray-500',
};

export function SensorCard({ entityId, title }: SensorCardProps): JSX.Element {
  const styles = useCardStyles();
  const entity = useEntity(entityId);
  const name = useEntityFriendlyName(entityId, title);
  const s = humanState(entity);
  const toneClass = TONE_COLOR[s.tone];

  const [trend, setTrend] = useState<Trend>('stable');
  const lastValueRef = useRef<number | null>(null);

  useEffect(() => {
    const parsed = Number(entity?.state);
    if (Number.isFinite(parsed)) {
      const prev = lastValueRef.current;
      if (prev !== null) {
        if (parsed > prev + 0.01) setTrend('up');
        else if (parsed < prev - 0.01) setTrend('down');
        else setTrend('stable');
      }
      lastValueRef.current = parsed;
    }
  }, [entity?.state]);

  const domain = domainForEntityId(entityId);
  const attrs = entity?.attributes as { device_class?: unknown } | undefined;
  const deviceClass = typeof attrs?.device_class === 'string' ? attrs.device_class : undefined;
  const Icon = sensorIconFor(domain, deviceClass);
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const isNumeric = Number.isFinite(Number(entity?.state));

  return (
    <div
      className={styles.container}
      data-domain={domain}
      data-entity-id={entityId}
      data-state={entity?.state ?? 'unknown'}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${
            styles.isDark ? 'bg-sky-500/15 text-sky-400' : 'bg-sky-50 text-sky-500'
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
        {isNumeric && (
          <div
            className={`flex items-center gap-1 text-xs ${
              trend === 'up' ? 'text-rose-400' : trend === 'down' ? 'text-sky-400' : styles.subtle
            }`}
          >
            <TrendIcon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div>
        <div className={`truncate text-sm font-medium ${styles.title}`}>{name}</div>
        <div className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>{s.text}</div>
      </div>
    </div>
  );
}
